import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { FileType, FileUploadViewModel, PromptComponent, ComponentType, InputType } from '@k12/models';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, MatButtonModule, MatIconModule, MatProgressBarModule, MatTooltipModule, DecimalPipe],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'lib-cfi-file-upload',
  styleUrl: './cfi-file-upload.component.scss',
  templateUrl: './cfi-file-upload.component.html'
})
export class CfiFileUploadComponent implements OnInit {
  @Input() allowPointerEvents!: boolean;
  @Input() configJson: PromptComponent | undefined = {
    header: '',
    instructions: '',
    order: 0,
    inputType: ComponentType.FileUpload,
    componentType: ComponentType.FileUpload,
    label: '',
    inputs: [
      {
        dataType: InputType.File,
        inputType: ComponentType.FileUpload,
        instructions: '',
        isRequired: true,
        fileSize: 0,
        fileType: [],
        max: 0,
        order: 0,
        label: '',
        options: [] // Set files and max size in code for now
      }
    ]
  };

  @Input() enrollmentBuilderView!: boolean;
  @Input() selectedFileTypes!: FileType[];
  @Input() isEnabled: boolean = true;
  @Input() showUploadProgress: boolean = true;

  @Output() cbConfig = new EventEmitter();
  @Output() decisionEvent = new EventEmitter();
  @Output() fileRejected = new EventEmitter<{ rejectedFiles: string[]; reason: string }>();
  @Output() filesAdded = new EventEmitter<File[]>();
  @Output() filesChanged = new EventEmitter<File[]>();
  /** Raised when the user asks to retry a failed upload; the owner re-sends the pending files. */
  @Output() retryRequested = new EventEmitter<void>();

  public vm!: FileUploadViewModel;
  public rejectedFileMessage: WritableSignal<string> = signal('');
  public isUploading: boolean = false;
  public currentUploadProgress: number = 0;

  public ngOnInit(): void {
    this.cbConfig.emit(this.configJson);

    this.vm = {
      defaultDownloadTemplateText: 'Download editable excel template',
      dropFilesHereText: 'Drag and drop file(s) here to upload',
      files: signal([]),
      fileSize: signal(''),
      // Driven by setUploadResult once the owning component has sent the bytes.
      uploadSucceeded: signal(false),
      uploadFailed: signal(false),
      supportedFileTypes: signal(''),
      totalSizePercent: signal(0),
      totalSize: signal(0)
    };
    // TODO: Support new Prompt data model
    // this.supportedFileTypes = this.configJson.inputs.value
    //   .map((fileType: any) => `.${fileType.name}`)
    //   .join(", ");
  }

  public choose(event: Event, callback: () => void) {
    callback();
  }

  public onRemoveTemplatingFile(file: File): void {
    // Remove specific file from the array
    const currentFiles = this.vm.files();
    const updatedFiles = currentFiles.filter((f) => f !== file);
    this.vm.files.set(updatedFiles);

    // Recalculate total size
    let totalSize = 0;
    updatedFiles.forEach((f) => {
      totalSize += f.size;
    });
    this.vm.totalSize.set(totalSize);
    this.vm.totalSizePercent.set((totalSize / (this.getMaxFileSizeMb() * 1024 * 1024)) * 100);

    // Update fileSize display for remaining files
    if (updatedFiles.length > 0) {
      const totalMb = totalSize / (1024 * 1024);
      const maxMb = this.getMaxFileSizeMb();
      this.vm.fileSize.set(`${totalMb.toFixed(1)}mb of ${maxMb}mb`);
    } else {
      this.vm.fileSize.set('');
    }
    this.filesChanged.emit(updatedFiles);
  }

  public getMaxFileSizeMb(): number {
    // Get max file size in MB from configJson
    const size = this.configJson?.inputs?.[0]?.fileSize;
    return typeof size === 'number' && !Number.isNaN(size) ? size : 0;
  }

  public getMaxNumberOfFiles(): number {
    const maxFiles = this.configJson?.inputs?.[0]?.max;
    return typeof maxFiles === 'number' && !Number.isNaN(maxFiles) && maxFiles > 0 ? maxFiles : Infinity;
  }

  public onSelectedFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);
    const currentFiles = this.vm.files();

    if (files.length + currentFiles.length > this.getMaxNumberOfFiles()) {
      const message = `You can only upload a maximum of ${this.getMaxNumberOfFiles()} file(s).`;
      this.rejectedFileMessage.set(message);
      return;
    }

    const { validFiles, rejectedFiles } = this.validateFiles(files, currentFiles);

    if (validFiles.length > 0) {
      this.addValidFiles(validFiles, currentFiles);
    }

    if (rejectedFiles.length > 0) {
      this.handleRejectedFiles(rejectedFiles);
    } else {
      this.rejectedFileMessage.set('');
    }

    // Clear the input to allow re-selecting the same files
    (event.target as HTMLInputElement).value = '';
  }

  private validateFiles(files: File[], currentFiles: File[]) {
    // Get allowed file extensions
    const fileTypes = (this.configJson?.inputs?.[0]?.fileType as string[]) ?? [];
    const allowedExtensions = fileTypes
      .filter((ext) => ext?.trim())
      .map((ext) => ext.trim().toLowerCase().replace(/^\./, ''));

    const maxSizeBytes = this.getMaxFileSizeMb() * 1024 * 1024;
    const currentTotalSize = currentFiles.reduce((sum, file) => sum + file.size, 0);

    const validFiles: File[] = [];
    const rejectedFiles: { file: File; reason: string }[] = [];

    files.forEach((file) => {
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

      if (allowedExtensions.length === 0 || !allowedExtensions.includes(fileExtension)) {
        rejectedFiles.push({ file, reason: 'Invalid file type' });
        return;
      }

      const newTotalSize = currentTotalSize + file.size + validFiles.reduce((sum, f) => sum + f.size, 0);
      if (newTotalSize <= maxSizeBytes) {
        validFiles.push(file);
      } else {
        rejectedFiles.push({ file, reason: 'Exceeds maximum file size' });
      }
    });

    return { validFiles, rejectedFiles };
  }

  private addValidFiles(validFiles: File[], currentFiles: File[]) {
    const allFiles = [...currentFiles, ...validFiles];
    this.vm.files.set(allFiles);

    const totalSize = allFiles.reduce((sum, file) => sum + file.size, 0);
    this.vm.totalSize.set(totalSize);

    const maxSizeBytes = this.getMaxFileSizeMb() * 1024 * 1024;
    this.vm.totalSizePercent.set(Math.min((totalSize / maxSizeBytes) * 100, 100));

    const totalMb = totalSize / (1024 * 1024);
    const maxMb = this.getMaxFileSizeMb();
    this.vm.fileSize.set(`${totalMb.toFixed(1)}mb of ${maxMb}mb`);

    this.filesAdded.emit(validFiles);
  }

  private handleRejectedFiles(rejectedFiles: { file: File; reason: string }[]) {
    const sizeRejected = rejectedFiles.filter((r) => r.reason === 'Exceeds maximum file size');
    const typeRejected = rejectedFiles.filter((r) => r.reason === 'Invalid file type');

    let message = '';
    if (typeRejected.length > 0) {
      const typeRejectedNames = typeRejected.map((r) => r.file.name).join(', ');
      const allowedTypes = ((this.configJson?.inputs?.[0]?.fileType as string[]) ?? ['*']).join(', ');
      message += `Files rejected due to invalid file type: ${typeRejectedNames}. Allowed types: ${allowedTypes}`;
    }

    if (sizeRejected.length > 0) {
      if (message) message += ' | ';
      const sizeRejectedNames = sizeRejected.map((r) => r.file.name).join(', ');
      message += `Files rejected due to size limit: ${sizeRejectedNames}`;
    }

    this.rejectedFileMessage.set(message);
    this.fileRejected.emit({
      rejectedFiles: rejectedFiles.map((r) => r.file.name),
      reason: rejectedFiles.map((r) => r.reason).join(', ')
    });

    setTimeout(() => {
      this.rejectedFileMessage.set('');
    }, 5000);
  }

  /**
   * Records the outcome of an upload the owning component performed. A failure message replaces the
   * rejection banner so the reason the server gave is what the user reads.
   */
  public setUploadResult(succeeded: boolean, failureMessage?: string): void {
    this.vm.uploadSucceeded.set(succeeded);
    this.vm.uploadFailed.set(!succeeded);

    if (!succeeded && failureMessage) {
      this.rejectedFileMessage.set(failureMessage);
    }
  }

  public retryUpload(): void {
    this.vm.uploadFailed.set(false);
    this.rejectedFileMessage.set('');
    this.retryRequested.emit();
  }

  public viewUpload(): void {
    // open the uploaded file
    // get a link to the file from the backend
    // window.open(this.file.name);
  }

  private formatSize(bytes: number): string {
    const k = 1024;
    const dm = 3;

    if (bytes === 0) {
      return `0 `;
    }

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formattedSize = Number.parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${formattedSize}`;
  }

  public formatFileSize(bytes: number): string {
    if (bytes === 0) return '0';
    const mb = bytes / (1024 * 1024);
    return mb.toFixed(1);
  }

  public dragOver = false;

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
    if ((event?.dataTransfer?.files?.length ?? -1) > 0) {
      const fileInputEvent = { target: { files: event.dataTransfer?.files } } as unknown as Event;
      this.onSelectedFile(fileInputEvent);
      event.dataTransfer?.clearData();
    }
  }

  public setUploadProgress(progress: number): void {
    this.currentUploadProgress = progress;
    this.vm.totalSizePercent.set(progress);
  }

  public setUploadingState(isUploading: boolean): void {
    this.isUploading = isUploading;
    if (!isUploading) {
      this.currentUploadProgress = 0;
      // Reset to file size percentage when not uploading
      const files = this.vm.files();
      if (files.length > 0) {
        let totalSize = 0;
        files.forEach((file) => {
          totalSize += file.size;
        });
        const maxSizeBytes = this.getMaxFileSizeMb() * 1024 * 1024;
        this.vm.totalSizePercent.set(Math.min((totalSize / maxSizeBytes) * 100, 100));
      } else {
        this.vm.totalSizePercent.set(0);
      }
    }
  }
}
